import type { FullTranslation } from "../../../lib/i18n"

import type { AuditKind } from "@/nango/types/apps.autoleads/autoleads/AuditTemplate"

export const AUDIT_T = {
  tabAudit: { FR: "Fichiers automatiques", EN: "Automatic files" },
  introTitle: { FR: "Audit SEO en PDF", EN: "SEO audit PDF" },
  introDesc: {
    FR: "Deux versions à vos couleurs : express (3 à 4 pages) et complète. Dans une séquence, une étape « Dépôt de fichier » en mode automatique génère celle de votre choix pour chaque lead et l'envoie dans l'email suivant.",
    EN: "Two versions in your colors: express (3 to 4 pages) and complete. In a sequence, a File upload step set to automatic generates the one you pick for each lead and sends it in the next email.",
  },
  emptyTitle: { FR: "Aucun modèle d'audit", EN: "No audit template yet" },
  emptyDesc: {
    FR: "Sans modèle, vos audits utilisent la mise en page par défaut. Créez le vôtre pour y mettre votre logo, vos couleurs et vos textes.",
    EN: "Without a template, your audits use the default layout. Create yours to add your logo, colors and wording.",
  },
  createTemplate: { FR: "Créer un modèle", EN: "Create a template" },
  kindMinimal: { FR: "Express", EN: "Express" },
  kindComplete: { FR: "Complet", EN: "Complete" },
  kindMinimalHint: {
    FR: "3 à 4 pages percutantes : votre site, ce qu'il coûte, les priorités.",
    EN: "3 to 4 punchy pages: the site, what it costs, the priorities.",
  },
  kindCompleteHint: {
    FR: "L'audit SEO en entier, point par point.",
    EN: "The whole SEO audit, point by point.",
  },
  versionLabel: { FR: "Version", EN: "Version" },
  prospectPdfLoading: {
    FR: "Génération du PDF, quelques secondes...",
    EN: "Generating the PDF, a few seconds...",
  },
  newTemplate: { FR: "Nouveau modèle", EN: "New template" },
  copySuffix: { FR: "(copie)", EN: "(copy)" },
  templateLabel: { FR: "Modèle", EN: "Template" },
  defaultBadge: { FR: "Par défaut", EN: "Default" },
  templateActions: { FR: "Actions du modèle", EN: "Template actions" },
  rename: { FR: "Renommer", EN: "Rename" },
  makeDefault: { FR: "Définir par défaut", EN: "Make default" },
  duplicate: { FR: "Dupliquer", EN: "Duplicate" },
  delete: { FR: "Supprimer", EN: "Delete" },
  deleteConfirmTitle: {
    FR: "Supprimer ce modèle ?",
    EN: "Delete this template?",
  },
  deleteConfirmDesc: {
    FR: "Les campagnes qui l'utilisent passeront sur le modèle par défaut de l'équipe.",
    EN: "Campaigns using it will switch to the team default template.",
  },
  cancel: { FR: "Annuler", EN: "Cancel" },
  save: { FR: "Enregistrer", EN: "Save" },
  nameLabel: { FR: "Nom du modèle", EN: "Template name" },
  tabIdentity: { FR: "Identité", EN: "Brand" },
  tabBlocks: { FR: "Blocs", EN: "Blocks" },
  logoLabel: { FR: "Logo", EN: "Logo" },
  logoHint: {
    FR: "Affiché sur la couverture. PNG, JPG ou SVG, 2 Mo maximum.",
    EN: "Shown on the cover. PNG, JPG or SVG, 2 MB max.",
  },
  logoUpload: { FR: "Importer un logo", EN: "Upload a logo" },
  logoReplace: { FR: "Remplacer", EN: "Replace" },
  logoRemove: { FR: "Retirer", EN: "Remove" },
  logoUploading: { FR: "Import...", EN: "Uploading..." },
  logoTooLarge: {
    FR: "Le logo doit faire 2 Mo maximum.",
    EN: "The logo must be 2 MB or smaller.",
  },
  logoInvalidType: {
    FR: "Le logo doit être une image.",
    EN: "The logo must be an image.",
  },
  colorsLabel: { FR: "Couleurs", EN: "Colors" },
  colorPrimary: { FR: "Principale", EN: "Primary" },
  colorPrimaryHint: {
    FR: "Couverture, titres, bouton",
    EN: "Cover, headings, button",
  },
  colorAccent: { FR: "Accent", EN: "Accent" },
  colorAccentHint: { FR: "Score, puces", EN: "Score, bullets" },
  colorText: { FR: "Texte", EN: "Text" },
  colorTextHint: { FR: "Corps du texte", EN: "Body text" },
  colorBackground: { FR: "Fond", EN: "Background" },
  colorBackgroundHint: { FR: "Fond des pages", EN: "Page background" },
  colorInvalid: {
    FR: "Format attendu : #rrggbb",
    EN: "Expected format: #rrggbb",
  },
  fontLabel: { FR: "Police", EN: "Font" },
  orientationLabel: { FR: "Orientation", EN: "Orientation" },
  orientationPortrait: { FR: "Portrait", EN: "Portrait" },
  orientationLandscape: { FR: "Paysage", EN: "Landscape" },
  whiteLabelLabel: { FR: "Marque blanche", EN: "White label" },
  whiteLabelHint: {
    FR: "Retire la mention « Réalisé avec Grezy » du PDF.",
    EN: "Removes the “Made with Grezy” mention from the PDF.",
  },
  whiteLabelScaleOnly: {
    FR: "Inclus dans l'offre Scale.",
    EN: "Included in the Scale plan.",
  },
  whiteLabelUpgrade: { FR: "Passer à l'offre Scale", EN: "Upgrade to Scale" },
  addBlock: { FR: "Ajouter un bloc", EN: "Add a block" },
  moveUp: { FR: "Monter", EN: "Move up" },
  moveDown: { FR: "Descendre", EN: "Move down" },
  removeBlock: { FR: "Retirer le bloc", EN: "Remove block" },
  showBlock: { FR: "Afficher le bloc", EN: "Show block" },
  noBlockSelected: {
    FR: "Sélectionnez un bloc pour le régler.",
    EN: "Select a block to tune it.",
  },
  blockCover: { FR: "Couverture", EN: "Cover" },
  blockScoreSummary: { FR: "Synthèse et score", EN: "Summary and score" },
  blockSiteToday: {
    FR: "Votre site aujourd'hui",
    EN: "Your website today",
  },
  blockThemes: { FR: "Points relevés", EN: "Findings" },
  blockTheme: { FR: "Un thème", EN: "One theme" },
  blockPagespeed: { FR: "Vitesse (PageSpeed)", EN: "Speed (PageSpeed)" },
  blockRank: { FR: "Positionnement Google", EN: "Google rankings" },
  blockText: { FR: "Texte libre", EN: "Free text" },
  blockCta: { FR: "Appel à l'action", EN: "Call to action" },
  blockAbout: { FR: "À propos de vous", EN: "About you" },
  blockPageBreak: { FR: "Saut de page", EN: "Page break" },
  fieldTitle: { FR: "Titre", EN: "Title" },
  fieldTitleHint: {
    FR: "Laissez vide pour le titre par défaut, traduit selon la langue du lead.",
    EN: "Leave empty for the default title, translated to the lead's language.",
  },
  fieldSubtitle: { FR: "Sous-titre", EN: "Subtitle" },
  fieldSubtitleHint: {
    FR: "Vide : l'adresse du site du prospect.",
    EN: "Empty: the prospect's website address.",
  },
  fieldIntro: { FR: "Introduction", EN: "Introduction" },
  fieldBody: { FR: "Texte", EN: "Text" },
  fieldAboutHint: {
    FR: "Vide : la proposition de valeur de votre produit.",
    EN: "Empty: your product's value proposition.",
  },
  fieldButtonLabel: { FR: "Texte du bouton", EN: "Button label" },
  fieldUrl: { FR: "Lien du bouton", EN: "Button link" },
  fieldUrlPlaceholder: { FR: "https://", EN: "https://" },
  fieldUrlHint: {
    FR: "Vide : le site de votre produit. Adresse complète en https://.",
    EN: "Empty: your product's website. Full https:// address.",
  },
  fieldShowLogo: { FR: "Afficher le logo", EN: "Show the logo" },
  fieldShowDate: { FR: "Afficher la date", EN: "Show the date" },
  fromSite: {
    FR: "Reprendre les couleurs et le logo de mon site",
    EN: "Use my website's colors and logo",
  },
  fromSiteLoading: { FR: "Lecture du site...", EN: "Reading the website..." },
  fromSiteDone: {
    FR: "Couleurs et logo repris de votre site",
    EN: "Colors and logo taken from your website",
  },
  bookingUrlLabel: {
    FR: "Lien de prise de rendez-vous",
    EN: "Booking link",
  },
  bookingUrlHint: {
    FR: "Le bouton « Prendre rendez-vous » du PDF. Repris de votre site quand il en propose un ; vide : votre site.",
    EN: "The PDF's “Book a meeting” button. Taken from your website when it offers one; empty: your website.",
  },
  screenshotsPending: {
    FR: "Captures du site de ce prospect en cours (moins d'une minute), l'aperçu se met à jour tout seul.",
    EN: "Taking this prospect's site screenshots (under a minute), the preview updates on its own.",
  },
  autosaveSaving: { FR: "Enregistrement...", EN: "Saving..." },
  autosaveSaved: { FR: "Enregistré", EN: "Saved" },
  autosaveFailed: { FR: "Non enregistré", EN: "Not saved" },
  openAsProspect: {
    FR: "Voir comme le prospect",
    EN: "View as the prospect",
  },
  fieldShowScreenshot: {
    FR: "Afficher les captures du site",
    EN: "Show the website screenshots",
  },
  fieldShowConsequences: {
    FR: "Expliquer l'impact pour l'entreprise",
    EN: "Explain the impact on the business",
  },
  fieldMaxFindings: { FR: "Points affichés", EN: "Points shown" },
  fieldExamples: {
    FR: "Exemples cités par point (recherches, questions IA)",
    EN: "Examples quoted per point (searches, AI questions)",
  },
  fieldExamplesNone: { FR: "Aucun", EN: "None" },
  screenshotHint: {
    FR: "Captures ordinateur et mobile prises lors de l'analyse PageSpeed. Sans capture, le bloc est retiré.",
    EN: "Desktop and mobile screenshots taken during the PageSpeed run. Without them, the block is left out.",
  },
  fieldShowScore: {
    FR: "Afficher le score sur 100",
    EN: "Show the score out of 100",
  },
  fieldShowDetails: {
    FR: "Afficher le détail de chaque point",
    EN: "Show each point's detail",
  },
  fieldSeverities: { FR: "Niveaux affichés", EN: "Levels shown" },
  severityHigh: { FR: "Prioritaire", EN: "Priority" },
  severityMedium: { FR: "À améliorer", EN: "To improve" },
  fieldThemes: { FR: "Thèmes affichés", EN: "Themes shown" },
  fieldThemesHint: {
    FR: "Aucun coché : tous les thèmes.",
    EN: "None ticked: every theme.",
  },
  fieldTheme: { FR: "Thème", EN: "Theme" },
  fieldMaxItems: { FR: "Points par thème", EN: "Points per theme" },
  fieldMaxItemsAll: { FR: "Tous", EN: "All" },
  fieldStrategies: { FR: "Appareils", EN: "Devices" },
  strategyMobile: { FR: "Mobile", EN: "Mobile" },
  strategyDesktop: { FR: "Ordinateur", EN: "Desktop" },
  variablesLabel: { FR: "Variables", EN: "Variables" },
  variablesHint: {
    FR: "Variables : cliquez pour les insérer dans le champ actif. Remplacées pour chaque lead.",
    EN: "Variables: click to insert into the active field. Filled in for each lead.",
  },
  pageBreakHint: {
    FR: "Le bloc suivant commence sur une nouvelle page.",
    EN: "The next block starts on a new page.",
  },
  previewTitle: { FR: "Aperçu", EN: "Preview" },
  previewOn: { FR: "Aperçu sur", EN: "Preview on" },
  previewSample: {
    FR: "Exemple (Boulangerie Martin)",
    EN: "Sample (Boulangerie Martin)",
  },
  previewLoading: {
    FR: "Mise à jour de l'aperçu...",
    EN: "Updating preview...",
  },
  draftInvalid: {
    FR: "Un lien de bouton n'est pas une adresse complète en https:// : corrigez-le pour mettre à jour l'aperçu et enregistrer.",
    EN: "A button link is not a full https:// address: fix it to update the preview and save.",
  },
  previewFailed: {
    FR: "L'aperçu n'a pas pu être généré.",
    EN: "The preview could not be generated.",
  },
  downloadPdf: { FR: "Télécharger le PDF", EN: "Download PDF" },
  downloadingPdf: { FR: "Génération...", EN: "Generating..." },
  themeGoogleProfile: { FR: "Fiche Google", EN: "Google profile" },
  themeVisibilityGeo: {
    FR: "Visibilité géographique",
    EN: "Geographic visibility",
  },
  themeVisibilityKeyword: {
    FR: "Visibilité mots-clés",
    EN: "Keyword visibility",
  },
  themeSeoTechnical: { FR: "Référencement technique", EN: "Technical SEO" },
  themeConversion: { FR: "Conversion", EN: "Conversion" },
  themeSpeed: { FR: "Performance", EN: "Performance" },
  themeDesign: { FR: "Design & ancienneté", EN: "Design & age" },
  themeAcquisition: { FR: "Acquisition", EN: "Acquisition" },
  themeTrust: { FR: "Réassurance", EN: "Trust" },
  themeCompliance: { FR: "RGPD", EN: "GDPR" },
  themeVisibilityAi: { FR: "Visibilité IA", EN: "AI visibility" },
  campaignTemplateLabel: { FR: "Modèle d'audit PDF", EN: "Audit PDF template" },
  campaignTemplateHint: {
    FR: "Les audits que génèrent les étapes « dépôt de fichier » de cette campagne (et le lien @audit_pdf, en version complète).",
    EN: "The audits this campaign's file steps generate (and the @audit_pdf link, in its complete version).",
  },
  campaignTemplateDefault: {
    FR: "Modèle par défaut de l'équipe",
    EN: "Team default template",
  },
  leadAuditPdf: { FR: "Rapport PDF", EN: "PDF report" },
  leadAuditPdfTitle: {
    FR: "Rapport d'audit · {name}",
    EN: "Audit report · {name}",
  },
  leadAuditPdfDescription: {
    FR: "Tel que le prospect le reçoit.",
    EN: "As the prospect receives it.",
  },
  leadAuditPdfOpenTab: { FR: "Ouvrir dans un onglet", EN: "Open in a tab" },
  leadAuditPdfDownload: { FR: "Télécharger", EN: "Download" },
  leadAuditPdfFailed: {
    FR: "Le PDF n'a pas pu être généré pour ce prospect.",
    EN: "The PDF could not be generated for this prospect.",
  },
  leadAuditPdfFailedHint: {
    FR: "Le service de rendu n'a pas répondu à temps. Réessayez dans un instant.",
    EN: "The rendering service did not answer in time. Try again in a moment.",
  },
  leadAuditPdfRetry: { FR: "Réessayer", EN: "Try again" },
  leadAuditPdfLoading: {
    FR: "Génération du PDF, cela prend quelques secondes…",
    EN: "Generating the PDF, this takes a few seconds…",
  },
  leadAuditPdfMobileHint: {
    FR: "L'aperçu n'est pas disponible sur mobile : ouvrez ou téléchargez le PDF.",
    EN: "The preview is not available on mobile: open or download the PDF.",
  },
} satisfies FullTranslation

export const KIND_LABELS = {
  minimal: "kindMinimal",
  complete: "kindComplete",
} as const satisfies Record<AuditKind, keyof typeof AUDIT_T>

export const KIND_HINTS = {
  minimal: "kindMinimalHint",
  complete: "kindCompleteHint",
} as const satisfies Record<AuditKind, keyof typeof AUDIT_T>
